import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useLatest } from 'hooks/useLatest';
import type { SearchJobsRefinementType } from 'modules/search/constants/refinements/searchJobsRefinementTypes';

export function useSearchRefinementsOverflow() {
  const overflowRefinementsRef = useRef<HTMLDivElement | null>(null);
  const [visibleRefinements, setVisibleRefinements] = useState<
    SearchJobsRefinementType[]
  >([]);

  const [searchParams] = useSearchParams();

  const resizeTimeoutIdRef = useLatest<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateVisibleRefinements = () => {
      if (!overflowRefinementsRef.current) return;

      const filterNodes =
        overflowRefinementsRef.current.querySelectorAll('[data-facet-type]');
      const newVisibleRefinements: SearchJobsRefinementType[] = [];

      filterNodes.forEach((node) => {
        node.setAttribute('data-hidden', 'true');
      });

      filterNodes.forEach((node) => {
        const filterType = node.getAttribute(
          'data-facet-type',
        ) as SearchJobsRefinementType;

        const filterAccordion = node.closest('details');

        if (!filterAccordion) return;

        if (!filterType || getComputedStyle(filterAccordion).display === 'none')
          return;
        newVisibleRefinements.push(filterType);
        node.setAttribute('data-hidden', 'false');
      });

      if (
        newVisibleRefinements.length !== visibleRefinements.length ||
        newVisibleRefinements.some(
          (filter, index) => filter !== visibleRefinements[index],
        )
      ) {
        setVisibleRefinements(newVisibleRefinements);
      }
    };

    updateVisibleRefinements();

    const observer = new MutationObserver(updateVisibleRefinements);
    if (overflowRefinementsRef.current) {
      observer.observe(overflowRefinementsRef.current, {
        attributes: true,
        childList: true,
        attributeFilter: ['style', 'checked'],
        subtree: true,
      });
    }

    window.addEventListener('resize', () => {
      if (resizeTimeoutIdRef.current) {
        clearTimeout(resizeTimeoutIdRef.current);
      }

      resizeTimeoutIdRef.current = setTimeout(
        () => updateVisibleRefinements(),
        20,
      );
    });

    return () => {
      observer.disconnect();
    };
  }, [resizeTimeoutIdRef, visibleRefinements]);

  const selectedRefinementValues = useMemo(() => {
    if (!overflowRefinementsRef.current) return 0;

    return visibleRefinements.reduce(
      (acc: number, filterType: SearchJobsRefinementType) => {
        let newAcc = acc;
        newAcc += searchParams.getAll(filterType).length;
        return newAcc;
      },
      0,
    );
  }, [visibleRefinements, searchParams]);

  return {
    overflowRefinementsRef,
    selectedRefinementValues,
    visibleRefinements,
  };
}
